import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Geolocation } from '@capacitor/geolocation';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getApiErrorMessage, MUTATION_OFFLINE_MESSAGE } from '@/lib/network/apiErrors';
import { getNetworkState } from '@/lib/network/connectivity';
import { reverseGeocode } from '@/lib/uploads';
import { useAuth } from '@/lib/auth';
import { fetchServiceSchema } from '@/hooks/useServiceConfig';
import MobileShell from '@/components/MobileShell';
import SchemaForm from '@/components/broadcast/SchemaForm';
import ServicePreferenceForm from '@/components/broadcast/ServicePreferenceForm';
import BookingRequirementsForm from '@/components/broadcast/BookingRequirementsForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CONSUMER_SERVICES, SERVICE_LABELS } from '@/lib/constants';

function QuoteSummary({ quote, serviceLabel, caseType, durationLabel }) {
  if (!quote) return null;
  const lineItems = quote.line_items || [];
  const serviceLine = lineItems.find((l) => l.key === 'service');
  const travelLine = lineItems.find((l) => l.key === 'travel');
  const taxLine = lineItems.find((l) => l.key === 'tax');

  return (
    <div className="resqly-card p-4 space-y-2" data-testid="booking-quote-summary">
      <div className="text-sm font-semibold text-slate-800">Booking summary</div>
      <div className="text-xs text-slate-600 space-y-1">
        <div><span className="font-medium">Service:</span> {serviceLabel}</div>
        {caseType && (
          <div>
            <span className="font-medium">Case:</span>{' '}
            {caseType === 'critical' ? 'Critical' : 'Non-critical'}
          </div>
        )}
        {durationLabel && (
          <div><span className="font-medium">Duration:</span> {durationLabel}</div>
        )}
        {serviceLine && (
          <div data-testid="quote-service-price">
            <span className="font-medium">Service price:</span> ₹{serviceLine.amount}
          </div>
        )}
        {travelLine && (
          <div>
            <span className="font-medium">Travel:</span>{' '}
            {quote.travel_included === false
              ? 'Calculated when provider accepts'
              : `₹${travelLine.amount}`}
          </div>
        )}
        {taxLine && (
          <div data-testid="quote-tax">
            <span className="font-medium">Tax:</span> ₹{taxLine.amount}
          </div>
        )}
        {quote.customer_total != null && (
          <div className="font-semibold text-slate-900 pt-1" data-testid="quote-total">
            Total: ₹{quote.customer_total}
            {quote.travel_included === false && (
              <span className="text-slate-500 font-normal"> (+ travel at acceptance)</span>
            )}
          </div>
        )}
      </div>
      {quote.requirements?.food_required && (
        <div className="text-xs text-amber-700">✓ Food arrangements required</div>
      )}
      {quote.requirements?.stay_required && (
        <div className="text-xs text-amber-700">
          ✓ Food, rest and accommodation required
        </div>
      )}
    </div>
  );
}

export default function BroadcastRequestScreen({ serviceKey }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const replacementSourceBookingId = searchParams.get('replacement') || '';
  const { me } = useAuth();
  const serviceMeta = CONSUMER_SERVICES.find((s) => s.key === serviceKey) || {
    key: serviceKey,
    label: SERVICE_LABELS[serviceKey] || serviceKey,
  };

  const [schema, setSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(true);
  const [preferences, setPreferences] = useState({});
  const [metadata, setMetadata] = useState({});
  const [address, setAddress] = useState(me?.location || '');
  const [coords, setCoords] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [caseType, setCaseType] = useState('');
  const [durationOptionId, setDurationOptionId] = useState('');
  const [customDurationMinutes, setCustomDurationMinutes] = useState('');
  const [foodAcknowledged, setFoodAcknowledged] = useState(false);
  const [stayAcknowledged, setStayAcknowledged] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const bookingRequirements = schema?.booking_requirements;
  const hasBookingRequirements = Boolean(bookingRequirements);

  const selectedOption = bookingRequirements?.duration_options?.find(
    (o) => o.id === durationOptionId,
  );
  const selectedDurationMinutes = useMemo(() => {
    if (!selectedOption) return 0;
    if (selectedOption.is_long_duration) return Number(customDurationMinutes) || 0;
    return selectedOption.duration_minutes || 0;
  }, [selectedOption, customDurationMinutes]);

  const durationLabel =
    quote?.duration_label ||
    selectedOption?.label ||
    (selectedDurationMinutes ? `${selectedDurationMinutes / 60} hours` : '');

  useEffect(() => {
    if (!replacementSourceBookingId) return;
    let cancelled = false;
    (async () => {
      try {
        await api.get(`/consumers/me/payment-entitlements/${replacementSourceBookingId}`);
        if (!cancelled) {
          toast.success('Your previous payment will be applied — no second payment required.');
        }
      } catch {
        if (!cancelled) {
          toast.error('Replacement payment entitlement is no longer available.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [replacementSourceBookingId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchServiceSchema(serviceKey);
      if (!cancelled) {
        if (!data) {
          toast.error('Could not load service form');
          navigate(`/consumer/service/${serviceKey}`, { replace: true });
          return;
        }
        setSchema(data);
        setLoadingSchema(false);
      }
    })();
    return () => { cancelled = true; };
  }, [serviceKey, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const perm = await Geolocation.requestPermissions();
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') return;
        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000,
        });
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng, accuracy_m: pos.coords.accuracy });
        try {
          const geo = await reverseGeocode(lat, lng);
          if (geo?.short || geo?.label) {
            setAddress((prev) => prev || geo.short || geo.label);
          }
        } catch {
          /* ignore */
        }
      } catch {
        /* manual address still allowed if coords missing - validation on submit */
      }
    })();
  }, []);

  useEffect(() => {
    if (!hasBookingRequirements) return;
    if (!durationOptionId) {
      setQuote(null);
      return;
    }
    if (selectedOption?.is_long_duration && !customDurationMinutes) {
      setQuote(null);
      return;
    }

    let cancelled = false;
    setQuoteLoading(true);
    (async () => {
      try {
        const { data } = await api.post('/service-requests/quote-preview', {
          service_key: serviceKey,
          case_type: caseType || undefined,
          duration_option_id: durationOptionId,
          custom_duration_minutes: selectedOption?.is_long_duration
            ? Number(customDurationMinutes)
            : undefined,
          provider_quantity: 1,
          food_requirement_acknowledged: foodAcknowledged,
          stay_requirement_acknowledged: stayAcknowledged,
          payment_entitlement_source_booking_id: replacementSourceBookingId || undefined,
        });
        if (!cancelled) setQuote(data.quote);
      } catch {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    hasBookingRequirements,
    serviceKey,
    caseType,
    durationOptionId,
    customDurationMinutes,
    foodAcknowledged,
    stayAcknowledged,
    selectedOption,
    selectedDurationMinutes,
    replacementSourceBookingId,
  ]);

  const canSubmit = useMemo(() => {
    if (!coords?.lat || !coords?.lng) return false;
    if (!hasBookingRequirements) return true;
    if (!durationOptionId) return false;
    if (selectedOption?.is_long_duration && !customDurationMinutes) return false;
    const foodThreshold = bookingRequirements.food_required_from_minutes || 360;
    const stayThreshold = bookingRequirements.stay_required_from_minutes || 1440;
    if (selectedDurationMinutes >= foodThreshold && !foodAcknowledged) return false;
    if (selectedDurationMinutes >= stayThreshold && !stayAcknowledged) return false;
    if (!quote?.customer_total && quoteLoading) return false;
    if (!quote && !quoteLoading) return false;
    return true;
  }, [
    coords,
    hasBookingRequirements,
    caseType,
    durationOptionId,
    selectedOption,
    customDurationMinutes,
    selectedDurationMinutes,
    foodAcknowledged,
    stayAcknowledged,
    bookingRequirements,
    quote,
    quoteLoading,
    replacementSourceBookingId,
  ]);

  const submit = async () => {
    if (!getNetworkState().isOnline) {
      toast.error(MUTATION_OFFLINE_MESSAGE);
      return;
    }
    if (!coords?.lat || !coords?.lng) {
      toast.error('Location is required. Enable GPS or set your location in profile.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        service_key: serviceKey,
        latitude: coords.lat,
        longitude: coords.lng,
        address,
        location_accuracy_m: coords.accuracy_m,
        preferences: Object.keys(preferences).length ? preferences : undefined,
        metadata: Object.keys(metadata).length ? metadata : undefined,
        provider_quantity: 1,
      };
      if (hasBookingRequirements) {
        if (caseType) body.case_type = caseType;
        body.duration_option_id = durationOptionId;
        if (selectedOption?.is_long_duration) {
          body.custom_duration_minutes = Number(customDurationMinutes);
        }
        body.food_requirement_acknowledged = foodAcknowledged;
        body.stay_requirement_acknowledged = stayAcknowledged;
      }
      if (replacementSourceBookingId) {
        body.payment_entitlement_source_booking_id = replacementSourceBookingId;
      }
      const { data } = await api.post('/service-requests', body);
      navigate(`/consumer/broadcast/${data.request.id}`, { replace: true });
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Your booking request could not be confirmed.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingSchema) {
    return (
      <MobileShell title={serviceMeta.label}>
        <div className="p-5 text-slate-500 text-sm">Loading request form…</div>
      </MobileShell>
    );
  }

  return (
    <MobileShell title={serviceMeta.label}>
      <div className="px-5 py-5 space-y-4" data-testid="broadcast-request-screen">
        {replacementSourceBookingId && (
          <div
            className="resqly-card p-4 bg-amber-50 border border-amber-200 text-sm text-slate-700"
            data-testid="replacement-request-notice"
          >
            <div className="font-semibold text-slate-900">Replacement provider search</div>
            <p className="mt-1">
              Your original payment will be applied automatically. You will not be charged again
              for the base booking.
            </p>
          </div>
        )}
        <div className="resqly-card p-4 flex items-start gap-3">
          <MapPin className="w-5 h-5 text-blue-700 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500 font-semibold uppercase">Service location</div>
            <Input
              data-testid="broadcast-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Address or area"
              className="mt-1"
            />
            {coords && (
              <p className="text-[10px] text-slate-400 mt-1">
                GPS: {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
              </p>
            )}
          </div>
        </div>

        {hasBookingRequirements && (
          <div className="resqly-card p-4">
            <h4 className="font-semibold text-slate-900 text-sm mb-3">Booking requirements</h4>
            <BookingRequirementsForm
              bookingRequirements={bookingRequirements}
              caseType={caseType}
              onCaseTypeChange={setCaseType}
              durationOptionId={durationOptionId}
              onDurationOptionChange={setDurationOptionId}
              customDurationMinutes={customDurationMinutes}
              onCustomDurationMinutesChange={setCustomDurationMinutes}
              foodAcknowledged={foodAcknowledged}
              onFoodAcknowledgedChange={setFoodAcknowledged}
              stayAcknowledged={stayAcknowledged}
              onStayAcknowledgedChange={setStayAcknowledged}
              selectedDurationMinutes={selectedDurationMinutes}
            />
          </div>
        )}

        {schema?.request_schema?.length > 0 && (
          <div className="resqly-card p-4">
            <h4 className="font-semibold text-slate-900 text-sm mb-3">Request details <span className="font-normal text-slate-400">(Optional)</span></h4>
            <SchemaForm
              schema={schema.request_schema}
              values={metadata}
              onChange={setMetadata}
              prefix="meta"
            />
          </div>
        )}

        {schema?.preference_schema?.length > 0 && (
          <div className="resqly-card p-4">
            <ServicePreferenceForm
              schema={schema.preference_schema}
              values={preferences}
              onChange={setPreferences}
            />
          </div>
        )}

        {hasBookingRequirements && (
          <QuoteSummary
            quote={quote}
            serviceLabel={serviceMeta.label}
            caseType={caseType || null}
            durationLabel={durationLabel}
          />
        )}

        <Button
          data-testid="broadcast-submit-request"
          onClick={submit}
          disabled={submitting || !canSubmit}
          className="w-full bg-blue-700 hover:bg-blue-800"
        >
          {submitting ? 'Starting search…' : 'Request help now'}
        </Button>
      </div>
    </MobileShell>
  );
}
